if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        if (!this.form) {
          console.warn('Product form element not found.');
          return;
        }

        const variantInput = this.variantIdInput;

        if (variantInput) {
          variantInput.disabled = false;
        }

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton =
          this.querySelector('[type="submit"]') ||
          this.form.querySelector('[type="submit"]');

        this.submitButtonText =
          this.submitButton?.querySelector('span') || null;

        if (
          this.cart &&
          this.submitButton
        ) {
          this.submitButton.setAttribute(
            'aria-haspopup',
            'dialog'
          );
        }

        this.hideErrors =
          this.dataset.hideErrors === 'true';

        this.error = false;
      }

      onSubmitHandler(event) {
        event.preventDefault();

        if (!this.form || !this.submitButton) return;

        if (
          this.submitButton.getAttribute('aria-disabled') ===
          'true'
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute(
          'aria-disabled',
          'true'
        );

        this.submitButton.classList.add('loading');

        const loadingSpinner =
          this.querySelector('.loading__spinner');

        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }

        // =========================================================
        // 🎁 BUNDLE & FREE GIFT LINKING LOGIC (ADDED HERE)
        // =========================================================
        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        // Generate unique Parent Bundle Key
        const parentBundleId = 'bundle_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);

        // Check Metafield Bundle Dataset from <product-form>
        const isBundleEnabled = this.dataset.enableBundle === 'true';
        const bundleProductsData = this.dataset.bundleProducts || '';
        const freeGiftsData = this.dataset.freeGifts || '';

        // Collect Form Properties (if any)
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propName = key.replace('properties[', '').replace(']', '');
            properties[propName] = value;
          }
        }

        // Set Internal Bundle Properties for Main Parent Item
        properties['_bundle_id'] = parentBundleId;
        properties['_bundle_type'] = 'parent';

        // Prepare Items Array for /cart/add.js
        const itemsToAdd = [];

        // 1. Add Main Parent Product
        itemsToAdd.push({
          id: parseInt(variantId, 10),
          quantity: quantity,
          properties: properties
        });

        // 2. Add Sub-items / Free Gifts / Add-ons from Metafields or Checkboxes
        if (isBundleEnabled || bundleProductsData || freeGiftsData) {
          // A) Free Gifts
          const giftIds = freeGiftsData.split(',').map(s => s.trim()).filter(Boolean);
          giftIds.forEach(giftId => {
            itemsToAdd.push({
              id: parseInt(giftId, 10),
              quantity: quantity,
              properties: {
                '_bundle_id': parentBundleId,
                '_bundle_type': 'child',
                '_parent_variant_id': variantId,
                '_is_free_gift': 'true'
              }
            });
          });

          // B) Bundle / Add-on Products
          const bundleIds = bundleProductsData.split(',').map(s => s.trim()).filter(Boolean);
          const checkedAddons = this.querySelectorAll('.js-bundle-addon-checkbox:checked');

          if (checkedAddons.length > 0) {
            checkedAddons.forEach(cb => {
              const addonId = cb.dataset.variantId || cb.value;
              if (addonId) {
                itemsToAdd.push({
                  id: parseInt(addonId, 10),
                  quantity: quantity,
                  properties: {
                    '_bundle_id': parentBundleId,
                    '_bundle_type': 'child',
                    '_parent_variant_id': variantId
                  }
                });
              }
            });
          } else if (bundleIds.length > 0) {
            bundleIds.forEach(addonId => {
              itemsToAdd.push({
                id: parseInt(addonId, 10),
                quantity: quantity,
                properties: {
                  '_bundle_id': parentBundleId,
                  '_bundle_type': 'child',
                  '_parent_variant_id': variantId
                }
              });
            });
          }
        }

        // Setup AJAX Request Payload
        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        };

        const requestPayload = {
          items: itemsToAdd
        };

        if (
          this.cart &&
          typeof this.cart.getSectionsToRender === 'function'
        ) {
          requestPayload.sections = this.cart
            .getSectionsToRender()
            .map((section) => section.id);
          requestPayload.sections_url = window.location.pathname;

          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        config.body = JSON.stringify(requestPayload);
        // =========================================================
        // 🎁 BUNDLE & FREE GIFT LINKING LOGIC END
        // =========================================================

        const linesUpdateDeferred =
          this.createCartLinesUpdateEvent(
            variantId,
            quantity
          );

        const cartAddUrl =
          window.routes?.cart_add_url ||
          '/cart/add.js';

        fetch(cartAddUrl, config)
          .then((response) => {
            if (!response.ok) {
              return response.json().then((data) => {
                throw {
                  responseData: data,
                  status: response.status
                };
              });
            }

            return response.json();
          })
          .then((response) => {
            /*
             * Shopify AJAX error response.
             */
            if (response.status) {
              if (
                typeof publish === 'function' &&
                window.PUB_SUB_EVENTS?.cartError
              ) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: variantId,
                  errors:
                    response.errors ||
                    response.description,
                  message: response.message
                });
              }

              const errorMessage =
                response.description ||
                response.message ||
                'Unable to add this product to cart.';

              this.handleErrorMessage(errorMessage);

              this.dispatchCartErrorEvent(
                errorMessage,
                'INVALID'
              );

              linesUpdateDeferred?.reject(
                new Error(errorMessage)
              );

              const soldOutMessage =
                this.submitButton.querySelector(
                  '.sold-out-message'
                );

              if (soldOutMessage) {
                this.submitButton.setAttribute(
                  'aria-disabled',
                  'true'
                );

                if (this.submitButtonText) {
                  this.submitButtonText.classList.add(
                    'hidden'
                  );
                }

                soldOutMessage.classList.remove('hidden');
              }

              this.error = true;
              return;
            }

            /*
             * No cart drawer or notification exists,
             * so redirect to cart page.
             */
            if (!this.cart) {
              this.resolveCartLinesUpdate(
                linesUpdateDeferred
              );

              window.location =
                window.routes?.cart_url || '/cart';

              return;
            }

            this.resolveCartLinesUpdate(
              linesUpdateDeferred
            );

            /*
             * Publish cart update for Dawn subscribers.
             */
            if (
              !this.error &&
              typeof publish === 'function' &&
              window.PUB_SUB_EVENTS?.cartUpdate
            ) {
              const publishResult = publish(
                PUB_SUB_EVENTS.cartUpdate,
                {
                  source: 'product-form',
                  productVariantId: variantId,
                  cartData: response
                }
              );

              if (
                publishResult &&
                typeof publishResult.then ===
                  'function'
              ) {
                publishResult.catch((error) => {
                  console.error(
                    'Cart update publish error:',
                    error
                  );
                });
              }
            }

            this.error = false;

            const quickAddModal =
              this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.renderCartContents(response);
                  }, 0);
                },
                { once: true }
              );

              if (
                typeof quickAddModal.hide ===
                'function'
              ) {
                quickAddModal.hide(true);
              }
            } else {
              this.renderCartContents(response);
            }
          })
          .catch((error) => {
            const responseData =
              error?.responseData || null;

            const errorMessage =
              responseData?.description ||
              responseData?.message ||
              error?.message ||
              'Network error. Please try again.';

            console.error(
              'Product add-to-cart error:',
              error
            );

            this.handleErrorMessage(errorMessage);

            this.dispatchCartErrorEvent(
              errorMessage,
              'SERVICE_UNAVAILABLE'
            );

            linesUpdateDeferred?.reject(
              new Error(errorMessage)
            );

            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove(
              'loading'
            );

            if (
              this.cart &&
              this.cart.classList.contains('is-empty')
            ) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute(
                'aria-disabled'
              );
            }

            if (loadingSpinner) {
              loadingSpinner.classList.add('hidden');
            }

            if (
              window.CartPerformance &&
              typeof CartPerformance.measureFromEvent ===
                'function'
            ) {
              CartPerformance.measureFromEvent(
                'add:user-action',
                event
              );
            }
          });
      }

      /*
       * Render cart drawer/cart notification safely.
       */
      renderCartContents(response) {
        if (!this.cart) return;

        if (
          typeof this.cart.renderContents ===
          'function'
        ) {
          this.cart.renderContents(response);
          return;
        }

        console.warn(
          'Cart component exists but renderContents() is unavailable.'
        );
      }

      /*
       * Product-info.js is calling this method.
       */
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (
          errorMessage &&
          this.errorMessage
        ) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }

      /*
       * Product-info.js is calling this method.
       */
      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          this.submitButton.setAttribute(
            'aria-disabled',
            'true'
          );

          if (
            text &&
            this.submitButtonText
          ) {
            this.submitButtonText.textContent = text;
          }

          return;
        }

        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute(
          'aria-disabled'
        );

        if (this.submitButtonText) {
          this.submitButtonText.textContent =
            window.variantStrings?.addToCart ||
            'Add to cart';

          this.submitButtonText.classList.remove(
            'hidden'
          );
        }

        const soldOutMessage =
          this.submitButton.querySelector(
            '.sold-out-message'
          );

        if (soldOutMessage) {
          soldOutMessage.classList.add('hidden');
        }
      }

      createCartLinesUpdateEvent(
        variantId,
        quantity
      ) {
        const CartLinesUpdateEvent =
          window.StandardEvents?.CartLinesUpdateEvent;

        if (!CartLinesUpdateEvent) return null;

        if (
          typeof CartLinesUpdateEvent.createPromise !==
          'function'
        ) {
          return null;
        }

        const deferred =
          CartLinesUpdateEvent.createPromise();

        try {
          this.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: [
                {
                  merchandiseId: variantId,
                  quantity
                }
              ],
              promise: deferred.promise
            })
          );
        } catch (error) {
          console.warn(
            'CartLinesUpdateEvent error:',
            error
          );

          return null;
        }

        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;

        const CartLinesUpdateEvent =
          window.StandardEvents?.CartLinesUpdateEvent;

        if (!CartLinesUpdateEvent) return;

        let pendingCartDataPromise;

        if (
          typeof window.CartItems !== 'undefined' &&
          typeof CartItems.fetchCartData ===
            'function'
        ) {
          pendingCartDataPromise =
            CartItems.fetchCartData();
        } else {
          const cartUrl =
            window.routes?.cart_url || '/cart';

          pendingCartDataPromise = fetch(
            cartUrl + '.json'
          ).then((response) => response.json());
        }

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) {
              throw new Error(
                'Missing currency in cart response'
              );
            }

            if (
              typeof CartLinesUpdateEvent.createCartFromAjaxResponse ===
              'function'
            ) {
              deferred.resolve({
                cart:
                  CartLinesUpdateEvent.createCartFromAjaxResponse(
                    cart
                  )
              });
            } else {
              deferred.resolve({ cart });
            }
          })
          .catch((error) => {
            deferred.reject(error);
          });
      }

      dispatchCartErrorEvent(message, code) {
        const CartErrorEvent =
          window.StandardEvents?.CartErrorEvent;

        if (!CartErrorEvent) return;

        try {
          this.dispatchEvent(
            new CartErrorEvent({
              error: message,
              code
            })
          );
        } catch (error) {
          console.warn(
            'CartErrorEvent dispatch error:',
            error
          );
        }
      }

      /*
       * Product-info.js uses:
       * this.productForm.variantIdInput.value
       */
      get variantIdInput() {
        if (!this.form) return null;

        return this.form.querySelector(
          'input[name="id"], select[name="id"]'
        );
      }
    }
  );
}